import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';

import type { ExportFormat, ExportFormatOption } from '../../types/export.types';

interface ExportFormatRadiosProps {
  formats: readonly ExportFormatOption[];
  format: ExportFormat;
  onFormatChange: (format: ExportFormat) => void;
  /** True when a feed template is applied, which is when the delimiter
   *  recommendation on a format means something. */
  feedTemplateApplied: boolean;
  /** Prefix for the radio ids, so two bars on one page never collide. */
  idPrefix?: string;
}

/**
 * The export bar's file-format radios (CSV / TSV / TXT) with the TSV
 * recommendation on the option it applies to. Shared by the batch browse page
 * and the catalog export page so both footers read the same.
 */
export function ExportFormatRadios({
  formats,
  format,
  onFormatChange,
  feedTemplateApplied,
  idPrefix = 'browse-export-format',
}: ExportFormatRadiosProps) {
  const { t } = useTranslation('exports');

  // Radios, like the drawer and the template picker: three options, all
  // visible, and the TSV recommendation sits on the option it applies to
  // rather than behind a closed select.
  return (
    <fieldset className="flex items-center gap-2">
      <legend className="sr-only">{t('estimate.format.label')}</legend>
      <RadioGroup
        value={format}
        onValueChange={(value) => {
          onFormatChange(value as ExportFormat);
        }}
        className="flex flex-wrap items-center gap-x-4 gap-y-1"
      >
        {formats.map((option) => {
          const id = `${idPrefix}-${option.code}`;
          const recommended = feedTemplateApplied && option.recommendedForFeedTemplates;
          return (
            <span key={option.code} className="flex items-center gap-1.5">
              <RadioGroupItem value={option.code} id={id} className="size-5 cursor-pointer" />
              <Label
                htmlFor={id}
                className="flex cursor-pointer items-center gap-1.5 text-xs font-normal"
              >
                <span className="text-foreground font-medium">{option.label}</span>
                <span className="text-muted-foreground">
                  {t(`estimate.format.hint.${option.code}`)}
                </span>
                {recommended && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="cursor-help text-[10px] font-normal">
                        {t('estimate.format.recommended')}
                      </Badge>
                    </TooltipTrigger>
                    {option.note !== '' && (
                      <TooltipContent className="max-w-xs">{option.note}</TooltipContent>
                    )}
                  </Tooltip>
                )}
              </Label>
            </span>
          );
        })}
      </RadioGroup>
    </fieldset>
  );
}
